// Helix·CX Owner Shell — ported from docs/design/handoff_owner_shell.
//
// Replaces the old "Verset" sidebar. Keeps the SAME props contract
// (title / crumbs / subtitle / actions / onLoggedOut / children /
// fullBleed) so every existing page adopts the new chrome with no
// per-page change. Chrome = the template's sidebar (the Helix mark at the
// top; product groups that open in place, or as a menu on the collapsed
// icon rail; the signed-in person with Profile / Sign out at the bottom; a
// sheet on a phone) | a main column with a top bar (collapse, search, theme,
// bell, workspace), the page-title row and a scrolling work area. Moved onto
// next-shadcn-admin-dashboard's sidebar on 5 Oct 2026
// (docs/testing/ui-migration/019-shell-tsx.md); /admin draws the same
// pieces (ops/admin/AdminShell.tsx, pass 020).
//
// The 7-pillar IA is the source of truth in NAV below; each leaf maps
// to an existing route or to /skeleton/:id for sections not built yet.
// Visual values come from shell-helix.css (read tokens, never hardcode).

import * as React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, EllipsisVertical, Monitor, Moon, Sun } from "lucide-react";
// No useNavigate: the only client-side navigation Shell itself performed was
// the post-sign-out push, which belongs to the /logout route now.
import { Link, useLocation } from "react-router-dom";
import { getActiveTenantSlug, opsFetch, setActiveTenantSlug } from "../api";
import { activePermissions, isPlatformStaff } from "../permissions";
import { landingFor } from "../landing";
import type { Me } from "../App";
import { ShellIcon } from "./shell/icons";
import NotificationBell from "./NotificationBell";
import GlobalSearchPalette from "./GlobalSearchPalette";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "./ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";
import { TooltipProvider } from "./ui/tooltip";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "./ui/sidebar";
import "../shell-helix.css";
import "../shell-helix-data.css";
import "../hx-controls.css"; // the shared control spec — loaded last so it wins

export interface BreadcrumbEntry {
  label: string;
  to?: string;
}

/** Where the IA matrix was opened from, carried in router state so the way back
 *  returns to that page and can name it. */
interface MatrixOrigin {
  from: string;
  label: string | null;
}

interface Props {
  me: Me;
  title?: string;
  crumbs?: BreadcrumbEntry[];
  subtitle?: ReactNode;
  actions?: ReactNode;
  onLoggedOut: () => Promise<void> | void;
  children: ReactNode;
  fullBleed?: boolean;
}

export interface NavLeaf {
  id: string;
  label: string;
  icon: string;
  to: string;
  badge?: string;
  /** Optional mini-heading rendered ABOVE this leaf inside its group —
   *  sub-structures a long group (e.g. Versatalk's Operations / Admin /
   *  To review) without changing the group/leaf data model. */
  heading?: string;
  /** Dynamic-RBAC permission required to see this leaf. Omitted = always
   *  shown (subject to the group's own gating). */
  permission?: string;
  /** Alternative to `permission`: the leaf is visible if the user holds ANY of
   *  these permissions (OR-gate). Used by the unified Conversations pillar,
   *  which appears when EITHER chat (conversations.read) OR voice (voice.read)
   *  is entitled. Takes precedence over `permission` when set. */
  anyPermission?: string[];
  /** GoZupees-internal surface: only Platform Admin (owner) and Platform Staff
   *  (admin — the forward-deployed engineer) may see it. NOT the same as an RBAC
   *  permission: a TENANT admin is the client's own business owner and may hold
   *  plenty of permissions, but must never see the engine beneath their product.
   *  Server enforces it too (voice.admin and friends); this only hides the link. */
  platformStaffOnly?: boolean;
  /** Hidden unless this person's JOB IS THE DESK — see `onAgentDesk` in
   *  filterNav. Not simply "holds an extension": a supervisor needs a line of
   *  their own, because listening, whispering and barging all ring the
   *  supervisor's phone first, and giving them one used to hand them the whole
   *  agent desk as a side effect. Owning a phone and working the queue are
   *  different facts and the nav had been reading one for the other. */
  requiresAgentDesk?: boolean;
  /** The inverse. Hidden FOR someone on the agent desk — used only to place one
   *  destination differently for the two kinds of reader, by declaring it twice
   *  under opposite gates. Conversations is the case: flat under Home for
   *  everyone else, inside Engage for an agent, who works it beside their desk.
   *
   *  The two declarations share an id ON PURPOSE. It is one destination, exactly
   *  one of the pair ever survives filterNav, and activeLeafId matches on the
   *  route — so a shared id is what makes the rendered row highlight whichever
   *  place it came from. Give them different ids and the agent's row goes dead.
   *
   *  The pair MUST read the same `onAgentDesk` value the other does. Gate one on
   *  "has a phone" and the other on "is an agent" and an admin with a line gets
   *  no Conversations row at all — neither branch claims them. */
  hiddenOnAgentDesk?: boolean;
}
export interface NavGroup {
  group: string | null;
  icon?: string;
  feature?: "billing"; // per-tenant feature flag — hidden unless enabled
  /** Hidden unless the user is a tenant_users member of the active workspace.
   *  Used for membership-holder surfaces (e.g. tenant Settings) that don't
   *  apply to the owner, who scopes via the switcher without a membership and
   *  manages tenants from Platform → Tenants instead. */
  requiresMembership?: boolean;
  /** With requiresMembership: also show the group to platform staff, with or
   *  without a workspace selected. For Teams, which staff manage inside a
   *  tenant and also review across all tenants, unlike tenant Settings, which
   *  they manage from Platform → Tenants instead. */
  orPlatformStaff?: boolean;
  /** Also show the group to a tenant admin of the active workspace
   *  (tenant_users.role='admin') who lacks `permission`. Mirrors the server's
   *  requireAdminOnTenant, which is what the key endpoints actually check. */
  orTenantAdmin?: boolean;
  /** Dynamic-RBAC permission required to see the whole group. When omitted,
   *  the group shows iff it has at least one visible leaf (after per-leaf
   *  permission filtering). Replaces the legacy ownerOnly/staffOnly/module
   *  flags — module entitlement now flows through the leaf/group permissions
   *  (voice.read/console.read/analytics.read), which the server only grants
   *  when the active tenant has the module. */
  permission?: string;
  items: NavLeaf[];
}

// ── PRODUCT-grouped nav (interim). Top-level groups = the four products;
//    each lists ALL its sub-pages so everything is reachable, and the whole
//    group shows/hides by the tenant's entitlement. Once everything is
//    discoverable we'll re-file these leaves under the Engage/Design/
//    Intelligence pillar IA with the same gating. ──
const NAV: NavGroup[] = [
  { group: null, items: [{ id: "home", label: "Home", icon: "home", to: "/home" }] },

  // ── Conversations — TWO DECLARATIONS, ONE DESTINATION ────────────────
  //
  // The inbox belongs in a different place depending on who is reading:
  //
  //   an AGENT works it beside their desk, so it sits inside Engage with the
  //   softphone, the tickets and the voicemail — the design is emphatic that a
  //   call is an event on the customer's timeline rather than a separate voice
  //   inbox, and splitting the two argued the opposite.
  //
  //   EVERYONE ELSE has no desk and no Engage pillar, so it is a flat row
  //   directly under Home — which is also where dev puts it, and the two shells
  //   are compared side by side here daily.
  //
  // NAV is a static array, so "the same leaf in two places" is written as two
  // entries under opposite gates: this one is `hiddenOnAgentDesk`, the one in
  // Engage is `requiresAgentDesk`. Exactly one survives filterNav for any given
  // person — they can never both render, and never both vanish.
  //
  // Same id, same route, same permissions in both. The id is shared so the
  // rendered row highlights (see hiddenOnAgentDesk on NavLeaf); the
  // permissions are identical so this places the destination WITHOUT changing
  // who may reach it.
  {
    group: null,
    items: [
      { id: "conversations", label: "Conversations", icon: "engage", to: "/conversations", anyPermission: ["conversations.read", "voice.read"], hiddenOnAgentDesk: true },
    ],
  },

  // ── Engage ───────────────────────────────────────────────────────────
  //
  // The pillar the agent lives in, from the design handoff's IA
  // (docs/design/rise-contact-center — prototype/cc/screens.js groups every
  // screen under Engage / Intelligence / AI / Admin / Integrations, and
  // roles.js gives the Agent six Engage destinations and nothing else).
  //
  // THIS GROUP IS THE AGENT'S DESK. Every leaf carries `requiresAgentDesk`, so
  // the whole pillar disappears for anyone whose job is not the queue — which is
  // NOT the same as "holds no extension". A supervisor needs a line of their own
  // to monitor with, and gating this on the phone handed them the desk as a side
  // effect; see onAgentDesk in filterNav. That is also
  // what lets Conversations sit in here for an agent and as a flat row for
  // everybody else: see the two-declaration note above.
  //
  // ⚠ It therefore still shows for a TENANT ADMIN who holds an extension, and
  // on technocraver every user does, the Account Manager included. That is a
  // fact about who was given a line, not about this nav: take the extension
  // away on Settings → Users and this group goes with it, no code change. The
  // gate cannot decide on its own whether a manager works the phones.
  {
    group: "Engage",
    icon: "engage",
    items: [
      // "Agent desktop", not "Softphone". The design names it that because it is
      // not a phone with a page around it: the softphone, the customer timeline
      // and the context pane are one frame, and an inbound call is the newest
      // event on the timeline the rep is already reading.
      { id: "vt-softphone", label: "Agent desktop", icon: "phone", to: "/voice/softphone", heading: "Agent", requiresAgentDesk: true },
      // The agent's copy of the inbox — same id, route and permissions as the
      // flat row above, gated the opposite way. See the note on that entry.
      { id: "conversations", label: "Conversations", icon: "engage", to: "/conversations", anyPermission: ["conversations.read", "voice.read"], requiresAgentDesk: true },
      // The rest of the agent's six. None is built; each renders a page naming
      // what it will be and which backlog item is in the way (AgentPending.tsx).
      // In the nav on purpose: leaving them out makes the shell look finished
      // when two of six exist, and hides where the voicemail an agent was
      // promised actually is. What they must NOT do is render the prototype's
      // sample screens — a voicemail list with three fictional customers in it
      // is worse than an empty one, because an agent works it.
      { id: "vt-tickets", label: "Tickets", icon: "tickets", to: "/voice/agent/tickets", requiresAgentDesk: true },
      // "VOICEMAIL", NOT "VOICEMAIL & CALLBACKS" — THE ROLE CANNOT SEE CALLBACKS.
      //
      // The callback queue is gated on voice.supervise, which was deliberately
      // taken away from Agent (see the Supervisor block below). This route
      // renders the voicemail inbox and nothing else, so the old label promised
      // an agent a list their own role forbids: they clicked it, found no
      // callbacks, and reported the page as broken. It was the label that was
      // broken.
      //
      // The supervisor entry for this same route already carries the honest
      // name for exactly this reason; the correction never reached here.
      //
      // ⚠ The design (pages/engage/screen-specs.ts, "voicemail") does want ONE
      // work list holding voicemails and queue callbacks together. That screen
      // is not built, and building it means deciding whether an agent may see
      // their own callbacks — an access decision, not a label. Until somebody
      // makes it, the nav says what the page shows.
      { id: "vt-voicemail", label: "Voicemail", icon: "phone", to: "/voice/agent/voicemail", requiresAgentDesk: true },
      { id: "vt-outbound", label: "Outbound preview", icon: "dialer", to: "/voice/agent/outbound-preview", requiresAgentDesk: true },
      { id: "vt-my-perf", label: "My performance", icon: "intel", to: "/voice/agent/my-performance", requiresAgentDesk: true },
    ],
  },

  // Dispositions & Tags manager — cross-channel taxonomy admin (dispositions.manage).
  {
    group: null,
    items: [
      { id: "dispositions", label: "Dispositions & Tags", icon: "tickets", to: "/dispositions", permission: "dispositions.manage" },
    ],
  },

  // ── Support Desk — the ITSM surface (14 Oct 2026). Tickets first (agent
  //    actions, conversation permissions), then the desk's configuration
  //    sections one below the other (support_desk.manage), mirroring the
  //    Metrics · Explorer group's shape. ─
  {
    group: "Support Desk",
    icon: "mail",
    permission: "conversations.read",
    items: [
      { id: "sd-new-ticket", label: "New ticket", icon: "sparkle", to: "/support-desk/tickets/new", permission: "conversations.write" },
      { id: "sd-all-tickets", label: "All tickets", icon: "tickets", to: "/support-desk/tickets", permission: "conversations.read" },
      { id: "sd-general", label: "General", icon: "settings", to: "/support-desk/general", permission: "support_desk.manage" },
      { id: "sd-reply", label: "Email", icon: "mail", to: "/support-desk/reply", permission: "support_desk.manage" },
      { id: "sd-macros", label: "Macros", icon: "bot", to: "/support-desk/macros", permission: "support_desk.manage" },
      { id: "sd-priorities", label: "Priorities & SLA", icon: "intel", to: "/support-desk/priorities", permission: "support_desk.manage" },
      { id: "sd-statuses", label: "Statuses", icon: "route", to: "/support-desk/statuses", permission: "support_desk.manage" },
      { id: "sd-fields", label: "Fields", icon: "grid", to: "/support-desk/fields", permission: "support_desk.manage" },
      { id: "sd-escalations", label: "Escalations", icon: "bell", to: "/support-desk/escalations", permission: "support_desk.manage" },
    ],
  },

  // ── Verset — chat / engage ──────────────────────────────────────────
  {
    group: "Verset · Chat",
    icon: "engage",
    items: [
      { id: "vs-contacts", label: "Contacts", icon: "customers", to: "/contacts", permission: "contacts.read" },
      { id: "vs-studio", label: "Agent Studio", icon: "spark", to: "/studio", permission: "agent_studio.read" },
    ],
  },

  // ── Versatalk — voice ───────────────────────────────────────────────
  {
    group: "Versatalk · Voice",
    icon: "phone",
    permission: "voice.read",
    items: [
      // Sectioned + per-section RBAC gating (voice.* keys). The group itself is
      // gated by voice.read (the module entitlement + base Call Monitoring
      // surface). Each section below is gated by its own voice.* permission so
      // a role can be given the whole product or just the front-line slice
      // (Call Monitoring). "Everyone sees all" today = the seeded voice roles
      // hold every voice.* key (see shared/permissions-catalog.ts + migration
      // 0039); tighten later from the Roles UI with no code change.

      // ── Overview ── (voice.overview) — exec/perf dashboards
      // Executive Dashboard lives on the merged /home; /voice/performance is the
      // standalone deep-link.
      { id: "vt-overview", label: "Overview", icon: "intel", to: "/voice", permission: "voice.overview" },
      // Merged into Overview (Performance Analytics tab) — 24 Sep 2026. Route kept.
      // { id: "vt-performance", label: "Performance Analytics", icon: "intel", to: "/voice/performance", heading: "Overview", permission: "voice.overview" },

      // ── Live desk ── (base — the human agent's in-browser softphone)
      //
      // No "Softphone agents" entry. Extensions are allocated when a user is
      // created with "Live-desk agent" ticked, and released when they are
      // removed, so there is nothing left to manage on a separate page — and a
      // second place to set who owns a line is what put two agents on one
      // extension. Who holds which number is shown on Settings → Users.
      // The agent's own destinations are NOT here — they moved to the Engage
      // group above, which is where the design's IA puts them. Versatalk keeps
      // the surfaces an agent must not reach: monitoring, analytics, quality and
      // the whole Admin section.

      // ── Call Monitoring ── NOT the group's base gate any more.
      //
      // These two show every agent's calls and every agent's audio, and the
      // design handoff's Agent card names that as the boundary the role must not
      // cross: "What this role never sees — other agents' calls or recordings".
      // Inheriting voice.read put both in front of every CSR, because voice.read
      // is what makes the Versatalk group appear at all and an agent needs it for
      // the softphone. So they carry their own keys:
      //
      //   Real-time Call Logs -> voice.supervise, which is what watching the
      //     floor IS. Held by platform staff; a tenant grants it to its leads.
      //   Recordings          -> voice.recordings.play, the G1 split. This also
      //     settles the open REPO-PLAN question about whether the PAGE should be
      //     gated or only playback: a list of who called whom, when, and for how
      //     long is itself customer data, and a page that renders it and then
      //     refuses to play is an odd half-answer.
      //
      // Neither removes anything from tenant-admin, AI Owner or platform staff —
      // they already hold these keys (0089). It removes both from Agent, which is
      // the point.
      { id: "vt-floor", label: "Floor", icon: "intel", to: "/voice/supervisor/floor", heading: "Supervisor", permission: "voice.supervise" },
      { id: "vt-agent-states", label: "Agents & states", icon: "agents", to: "/voice/supervisor/agents", permission: "voice.supervise" },
      // Beside the floor, not under Analytics: this is what is happening now,
      // and the wallboard next to it counts the same calls without the words.
      // voice.supervise — reading somebody else's live conversation is the same
      // authority as listening to it.
      { id: "vt-live-transcripts", label: "Live transcripts", icon: "intel", to: "/voice/supervisor/live", permission: "voice.supervise" },
      { id: "vt-callbacks", label: "Callbacks", icon: "phone", to: "/voice/supervisor/callbacks", permission: "voice.supervise" },
      // MAILBOX CONFIG NEEDS A SUPERVISOR DOOR OF ITS OWN.
      //
      // The same page is the agent's inbox AND the only mailbox editor, and the
      // agent entry carries requiresAgentDesk — which an admin never is. So the
      // two halves locked each other out: the agent saw the page and the server
      // refused their writes, while the only person allowed to write could not
      // reach it. Nobody could create a mailbox, and a caller's message would be
      // recorded by the switch and then discarded on upload as "no_mailbox".
      //
      // Listed here rather than un-gating the agent entry, because a supervisor
      // does not want "& callbacks" twice — Callbacks is the row above. Same
      // route, honest label: what they come here for is the mailboxes.
      // Back to one key. It carried voice.manage_routing too for a while, to let
      // an admin reach the mailbox editor that lived on this page — a third
      // patch on the same spot, and it put the entry under the wrong
      // mini-heading for exactly that reader (headings belong to a leaf, so
      // "Supervisor" vanishes with vt-floor and the survivors re-parent).
      // The editor is now its own page under Telephony, so this is the
      // supervisor's view of the INBOX and nothing else.
      { id: "vt-voicemail-sup", label: "Voicemail", icon: "phone", to: "/voice/agent/voicemail", permission: "voice.supervise" },
      // AND TICKETS NEEDS THE SAME DOOR, FOR THE SAME REASON.
      //
      // Tickets is now the CALL list — its state lens is resolution_status, and
      // its Owner column is a picker that only voice.supervise can use. But the
      // agent entry carries requiresAgentDesk, and a supervisor is deliberately
      // NOT on the desk (see onAgentDesk below: holding a phone and working the
      // queue are different facts). So the one person the page's own control is
      // built for had no way in — the page answered them perfectly over a URL
      // they could not reach from the rail.
      //
      // Exactly the fault the note above this describes for the mailbox editor,
      // reappearing on the next page along. Same remedy: a second entry on the
      // supervisor's own key, same route, listed here rather than un-gating the
      // agent's, so neither reader gets the other's rail.
      { id: "vt-tickets-sup", label: "Tickets", icon: "tickets", to: "/voice/agent/tickets", permission: "voice.supervise" },
      // READING THE FLOOR IS NOT COMMANDING IT.
      //
      // This was voice.supervise alone, and that key is AUTHORITY over other
      // people's calls — standing an agent down, Listen / Whisper / Barge. A
      // live list of calls in progress is a read, and its own section proves
      // the point: Recordings & Transcripts sits beside it on
      // voice.recordings.play, so the pair split down the middle the moment
      // supervise was taken off a role. An account manager was left with half a
      // Call Monitoring section and no way to say why.
      //
      // voice.overview is the right second key: it is the cross-agent reporting
      // gate an account manager, ops manager and executive hold — and which the
      // Agent role deliberately does not, so the boundary the design draws
      // ("never sees other agents' calls or recordings") still holds.
      // ONE HUB, SO ONE GATE — and the gate is the union of the three the four
      // old pages carried.
      //
      // Active, Transferred, Recordings & Transcripts and Archived are now tabs
      // on /voice/calls (VoiceCalls.tsx, 24 Sep 2026); the old routes still
      // resolve. Merging the pages merges their permissions, and the safe
      // direction is the union: anyone who could open ANY of the four still
      // reaches the hub, and nobody gains a page they could not already open —
      // they gain tabs beside it. It is deliberately NOT the group's bare
      // voice.read, which every agent holds for the softphone; that would put
      // the whole floor's calls and recordings in front of every CSR, which is
      // the one boundary the design's Agent card draws by name.
      //
      // ⚠ The looser half of the trade: a voice.recordings.play holder now sees
      // the live Active/Transferred tabs too. Worth revisiting by gating the
      // TABS rather than the hub, which is where this really belongs.
      { id: "vt-calls", label: "Calls", icon: "phone", to: "/voice/calls", heading: "Call Monitoring", anyPermission: ["voice.supervise", "voice.overview", "voice.recordings.play"] },

      // ── Agent Analytics ── (voice.analytics)
      { id: "vt-agents", label: "Agents", icon: "agents", to: "/voice/agents", permission: "voice.analytics" },
      // Merged into Agents (Roster / People / AI agents / FCR & Gaps / Transfer Insights tabs) — 24 Sep 2026. Routes kept.
      // { id: "vt-agent-perf", label: "Agent Performance", icon: "agents", to: "/voice/agents/performance", heading: "Agent Analytics", permission: "voice.analytics" },
      // { id: "vt-fcr-gaps", label: "FCR & Gaps", icon: "intel", to: "/voice/agents/fcr", permission: "voice.analytics" },
      // { id: "vt-roster", label: "Agent Roster", icon: "agents", to: "/voice/agents/list", permission: "voice.analytics" },
      // { id: "vt-transfers", label: "Transfer Insights", icon: "route", to: "/voice/agents/transfers", permission: "voice.analytics" },

      // ── Quality ── (voice.quality)
      // Merged into Agents (Evaluations tab) — 24 Sep 2026. Route kept.
      // { id: "vt-evals", label: "Evaluations", icon: "tickets", to: "/voice/qa/evaluations", heading: "Quality", permission: "voice.quality" },

      // Operations is a TAB of Engine & Operations (vt-admin, below) — the same
      // OpsBody, opened a second way. When VoiceAdminHub folded the four admin
      // pages into tabs (24 Sep), health/config/engine lost their own entries and
      // this one was missed. /voice/admin/operations still routes, for bookmarks.
      // Its "Admin" heading moved to vt-telephony, which is where the voice.admin
      // leaves actually are; here it headed nothing but this one entry.
      // { id: "vt-operations", label: "Operations", icon: "settings", to: "/voice/admin/operations", heading: "Admin", permission: "voice.admin" },
      // ── Outbound ── these two sit HERE, and the position is the fix.
      //
      // A heading in this nav belongs to a LEAF, and every leaf after it inherits
      // it until the next one. These were filed between Hours and Wrap-up, under
      // Telephony, which read correctly for an admin and wrongly for everybody
      // else: a supervisor holds manage_campaigns but not manage_routing, so
      // Numbers / Queues / Hours all filtered out and took the Telephony heading
      // with them — leaving Campaigns and DNC to fall under CALL MONITORING.
      // Running a dialler campaign is not monitoring calls.
      //
      // Giving vt-campaigns its own heading where it stood would have fixed the
      // supervisor and broken the admin, because Wrap-up, Mailboxes, E911,
      // Billing, Config and the rest would then have inherited "Outbound".
      // Moving the pair ABOVE vt-numbers is what makes the heading safe: the
      // Telephony heading re-establishes itself on the very next leaf, so
      // nothing can leak out of this section for any role.
      //
      // Both keys are voice.manage_campaigns — one gate over the whole outbound
      // surface, who you may call and when. See permissions-catalog.ts.
      { id: "vt-campaigns", label: "Campaigns & lists", icon: "dialer", to: "/voice/admin/campaigns", heading: "Outbound", permission: "voice.manage_campaigns" },
      { id: "vt-dnc", label: "DNC & calling hours", icon: "shield", to: "/voice/admin/dnc", permission: "voice.manage_campaigns" },
      // Which team each phone number rings, and the caller ID each team presents.
      //
      // NOT voice.admin — that is deliberately platform-staff only
      // (permissions-catalog.ts keeps the internal Admin section away from
      // customers), and gating this on it would hide a TENANT'S OWN telephony
      // configuration from the tenant admin who is supposed to manage it.
      //
      // But it was voice.read, and that was too wide in the other direction: the
      // design's Agent card says the role never sees "queue and routing config",
      // and this page is exactly that — which number rings which team, and the
      // caller ID each team presents. voice.manage_routing is the same key Queues
      // uses, tenant-admin already holds it (0089), and Agent does not. The
      // read-only render for non-admins (`canManage`) and the server-side
      // voice.manage_routing on every write both stay as they were.
      { id: "vt-numbers", label: "Numbers", icon: "phone", to: "/voice/admin/numbers", heading: "Telephony", permission: "voice.manage_routing" },
      // Changing a queue reroutes live calls and changes what every caller hears,
      // so it sits behind voice.manage_routing rather than voice.read — the first
      // surface to use one of the contact-centre keys from migration 0088.
      { id: "vt-queues", label: "Queues", icon: "queue", to: "/voice/admin/queues", permission: "voice.manage_routing" },
      // "& access" dropped with the SSO card: the page's access half stored
      // settings no login path reads, and the label promised it anyway. The
      // server API and table remain for when SSO is actually built (H5).
      { id: "vt-wrap", label: "Wrap-up", icon: "clock", to: "/voice/admin/wrap", permission: "voice.manage_routing" },
      // Where a call lands when nobody answers — the last step of the same
      // question Numbers, Queues and Hours answer, so it carries the same key
      // and sits with them. It was a card on the agent's Voicemail page until
      // three separate lock-outs made it clear that one URL cannot serve an
      // agent's inbox, a supervisor's oversight and an admin's configuration.
      { id: "vt-mailboxes", label: "Mailboxes", icon: "phone", to: "/voice/admin/voicemail", permission: "voice.manage_routing" },
      // Statutory (Kari's Law, RAY BAUM'S Act). Its own permission because a
      // wrong dispatchable address sends responders to the wrong place — that is
      // not the same authority as managing call routing.
      { id: "vt-e911", label: "E911", icon: "shield", to: "/voice/admin/e911", permission: "e911_admin" },
      // What the switch actually is. Names carrier IPs and registration state, so
      // it sits with the other voice.admin surfaces rather than voice.read.
      // "Outage & MyRise" removed from the nav (page deleted, restorable from
      // git). EVERYTHING BEHIND IT STILL RUNS: the outage push endpoint, the
      // agent pane's outage strip, the integrations API and the saved config
      // are all live — the page was only the admin's editor for them. Rebuild
      // it when Rise's real endpoints make the MyRise half worth a screen.
      // ── Admin ── the voice.admin leaves, last in the group so the heading
      // cannot leak onto anything after it: a role without voice.admin loses all
      // three together, and nothing follows them.
      { id: "vt-telephony", label: "Telephony path", icon: "route", to: "/voice/admin/telephony-path", heading: "Admin", permission: "voice.admin" },
      { id: "vt-billing", label: "Billing & Usage", icon: "card", to: "/voice/admin/billing", permission: "voice.admin" },
      { id: "vt-admin", label: "Engine & Operations", icon: "settings", to: "/voice/admin", permission: "voice.admin" },
      // { id: "vt-health", label: "System Health", icon: "settings", to: "/voice/admin/system-health", permission: "voice.admin" },
      // { id: "vt-config", label: "Config", icon: "settings", to: "/voice/admin/config", permission: "voice.admin" },
      // GoZupees-internal. The client sees "VersaTalk"; this is where we see the
      // vendor, the provider agent ids, and every version. Never shown to a tenant.
      // { id: "vt-engine", label: "Voice Engine", icon: "settings", to: "/voice/admin/engine", permission: "voice.admin", platformStaffOnly: true },
      // { id: "vt-archived", label: "Archived Calls", icon: "box", to: "/voice/operations/archived", permission: "voice.admin" },

      // ── Parked / "To review" ── stub or hardcoded-demo pages in BOTH source
      // apps. Hidden from the nav for CityFibre's voice-only go-live (the routes
      // stay reachable by deep-link). Re-file into a section above + give each a
      // voice.* permission when it's built for real:
      //   /voice/intelligence/conversation   Conversation Intelligence
      //   /voice/intelligence/decision       Decision Intelligence
      //   /voice/intelligence/operational    Operational Insights
      //   /voice/qa/evaluation-analytics     Evaluation Analytics
      //   /voice/qa/quality                  Quality Assurance
      //   /voice/qa/reports                  QA Reports
      //   /voice/admin/reports               Reports
    ],
  },

  // ── Versight — analytics ────────────────────────────────────────────
  {
    group: "Versight · Analytics",
    icon: "intel",
    permission: "analytics.read",
    // Trimmed to the essentials 25 Sep 2026: every analytics table was empty on
    // dev and the S3 → Deepgram ingestion pipeline is not configured. The
    // commented leaves are HIDDEN, not removed — their pages and routes still
    // exist (App.tsx), stay on the shared theme, and open by URL. Uncomment a
    // line to bring its page back into the sidebar.
    items: [
      { id: "vi-overview", label: "Overview", icon: "intel", to: "/analytics" },
      // { id: "vi-executive", label: "Executive Dashboard", icon: "grid", to: "/analytics/leadership/executive" },
      // { id: "vi-team-leader", label: "Team Leader", icon: "agents", to: "/analytics/leadership/team-leader" },
      // { id: "vi-scorecard", label: "Performance Scorecard", icon: "intel", to: "/analytics/leadership/scorecard" },
      { id: "vi-coaching", label: "Coaching", icon: "tickets", to: "/analytics/leadership/coaching" },
      { id: "vi-investigation", label: "Call Investigation", icon: "search", to: "/analytics/operations/call-investigation" },
      { id: "vi-reports", label: "Analytics & Reports", icon: "book", to: "/analytics/operations/reports" },
      // { id: "vi-ai-vs-human", label: "AI vs Human", icon: "route", to: "/analytics/operations/ai-vs-human" },
      // { id: "vi-kpi", label: "KPI Targets", icon: "intel", to: "/analytics/qa/kpi-targets" },
      // { id: "vi-alerts", label: "Alert Thresholds", icon: "bell", to: "/analytics/qa/alert-thresholds" },
      { id: "vi-governance", label: "Compliance & Governance", icon: "shield", to: "/analytics/qa/governance" },
      // { id: "vi-notifications", label: "Notification Rules", icon: "bell", to: "/analytics/qa/notifications" },
      { id: "vi-ingestion", label: "Call Ingestion", icon: "flow", to: "/analytics/admin/ingestion" },
      // { id: "vi-zendesk", label: "Zendesk Import", icon: "plug", to: "/analytics/admin/zendesk" },
      // { id: "vi-models", label: "AI Models", icon: "cpu", to: "/analytics/admin/ai-models" },
      // { id: "vi-properties", label: "Properties", icon: "box", to: "/analytics/admin/properties" },
      { id: "vi-dsar", label: "DSAR & Data Requests", icon: "book", to: "/analytics/admin/dsar" },
      // { id: "vi-health", label: "System Health", icon: "settings", to: "/analytics/admin/system-health" },
      // { id: "vi-risk", label: "Risk Insights", icon: "shield", to: "/analytics/admin/risk-insights" },
    ],
  },

  // ── Versense — console ──────────────────────────────────────────────
  {
    group: "Versense · Console",
    icon: "channels",
    permission: "console.read",
    items: [
      { id: "vn-live", label: "Live Calls", icon: "phone", to: "/console" },
      { id: "vn-exec", label: "Executive", icon: "grid", to: "/console/dashboard/executive" },
      { id: "vn-team-leader", label: "Team Leader", icon: "agents", to: "/console/dashboard/team-leader" },
      { id: "vn-member", label: "Member", icon: "customers", to: "/console/dashboard/member" },
      { id: "vn-command", label: "Command Center", icon: "grid", to: "/console/operations/command" },
      { id: "vn-outage", label: "Outage & Service", icon: "shield", to: "/console/operations/outage" },
      { id: "vn-dispatch", label: "Dispatch & Field", icon: "route", to: "/console/operations/dispatch" },
      { id: "vn-activation", label: "Activation Watch", icon: "spark", to: "/console/operations/activation" },
      { id: "vn-scorecard", label: "Personalized Scorecard", icon: "intel", to: "/console/performance/scorecard" },
      { id: "vn-coaching", label: "Coaching Plan", icon: "tickets", to: "/console/performance/coaching" },
      { id: "vn-training", label: "Training / Cert", icon: "book", to: "/console/performance/training" },
      { id: "vn-inbox", label: "Unified Inbox", icon: "mail", to: "/console/messages/inbox" },
      { id: "vn-channels", label: "Channel Management", icon: "channels", to: "/console/messages/channels" },
      { id: "vn-routing", label: "Routing Rules", icon: "route", to: "/console/messages/routing" },
      { id: "vn-templates", label: "Templates", icon: "book", to: "/console/messages/templates" },
      { id: "vn-root-cause", label: "Root Cause Analysis", icon: "intel", to: "/console/insights/root-cause" },
      { id: "vn-community", label: "Community Scorecards", icon: "customers", to: "/console/insights/community" },
      { id: "vn-call-level", label: "Call Level Insights", icon: "intel", to: "/console/insights/call-level" },
      { id: "vn-integrations", label: "System Integrations", icon: "settings", to: "/console/settings/integrations" },
      { id: "vn-notifications", label: "Notification Rules", icon: "bell", to: "/console/settings/notifications" },
      { id: "vn-compliance", label: "Compliance", icon: "shield", to: "/console/settings/compliance" },
      { id: "vn-sessions", label: "Sessions", icon: "grid", to: "/console/sessions" },
    ],
  },

  // ── CX control plane — journey orchestration above BSS/OSS ──────────
  //    Gated by cx.read, which is AND-gated against the `cx` module
  //    entitlement, so it stays invisible to every tenant without it.
  {
    group: "Helix · CX",
    icon: "route",
    permission: "cx.read",
    items: [
      // Two surfaces, not one: watching the AI (observability — explain,
      // override) and assisting a human on the call (assist — suggest, act).
      // See docs/comcast-demo-screens-spec.md §5a.
      { id: "cx-live-ai", label: "Live · AI agent", icon: "bot", to: "/cx/live/ai" },
      { id: "cx-live-human", label: "Live · Human agent", icon: "phone", to: "/cx/live/human" },
      // The §3.2/§3.6 evidence page: live policies, decision traces, controls.
      { id: "cx-governance", label: "Governance", icon: "intel", to: "/cx/governance" },
      // The lifecycle dashboard over the journey ledger (Attract/Interact/
      // Transact/React) — the analytics answer to the partner's mock screens.
      { id: "cx-analytics", label: "Journey Analytics", icon: "spark", to: "/cx/analytics" },
    ],
  },

  // ── Workflows — automation builder ──────────────────────────────────
  {
    group: "Workflows · Automation",
    icon: "flow",
    permission: "workflow.read",
    items: [
      { id: "wf-list", label: "Workflows", icon: "flow", to: "/workflow" },
      { id: "wf-executions", label: "Executions", icon: "grid", to: "/workflow/executions" },
    ],
  },

  // ── Metrics & Extractors — cross-module explorer + dashboards ────────
  // Deliberately its own top-level pillar: it queries voice + chat +
  // analytics uniformly, so it is NOT nested under any single product.
  {
    group: "Metrics · Explorer",
    icon: "intel",
    permission: "insights.read",
    items: [
      { id: "mx-explore", label: "Explorer", icon: "intel", to: "/explore" },
      { id: "mx-dashboards", label: "Dashboards", icon: "grid", to: "/dashboards" },
      { id: "mx-metrics", label: "Metrics", icon: "settings", to: "/metrics" },
      { id: "mx-extractors", label: "Extractors", icon: "sparkle", to: "/extractors" },
      { id: "mx-derivations", label: "Derivations", icon: "route", to: "/derivations" },
    ],
  },

  // ── Integrations — canonical per-tenant integration config (secrets +
  //    tenant config). Flat pill, RBAC-gated on the dedicated integrations
  //    permissions (read OR manage); the page + routes enforce edit rights.
  //    Replaces the per-module integration surfaces. ─
  {
    group: null,
    items: [
      { id: "integrations", label: "Integrations", icon: "plug", to: "/integrations", anyPermission: ["integrations.read", "integrations.manage"] },
    ],
  },

  // ── Billing — real-time usage + bill. Flat pill, visible to anyone
  //    scoped to a tenant (tenant admins see their own bill). Rate-card
  //    edits inside are staff-gated server-side. ─
  {
    group: null,
    feature: "billing",
    items: [
      { id: "billing", label: "Billing", icon: "card", to: "/billing" },
    ],
  },

  // ── Settings — Platform Staff managing their own client. Flat pill
  //    (group: null) so it renders like Home — no caret, no hover flyout.
  //    A tenant admin sees it too, but the page shows them the API Keys tab
  //    only: they mint and revoke their own keys, nothing else in settings. ─
  {
    group: null,
    permission: "tenant_settings.manage",
    orTenantAdmin: true,
    requiresMembership: true,
    items: [
      { id: "tn-settings", label: "Settings", icon: "settings", to: "/tenant-settings" },
    ],
  },

  // Teams — agent teams and their member roster. The page has existed at /teams
  // with NO nav entry anywhere, so it was reachable only by typing the URL. That
  // is now a dead end users actually hit: teams drive chat handoff routing AND
  // which phone number rings whom (Versatalk → Telephony → Numbers), so there
  // has to be a way in.
  {
    group: null,
    permission: "teams.manage",
    requiresMembership: true,
    // Restored for tenant admins (15 Oct 2026): the owner's direction is to
    // converge on THIS teams surface (it also drives voice routing — which
    // phone number rings whom), rather than the Support Desk's own tab.
    // Platform staff keep it too for the "All tenants" onboarding rollup.
    orPlatformStaff: true,
    items: [
      { id: "tn-teams", label: "Teams", icon: "agents", to: "/teams", permission: "teams.manage" },
      // General workspace configuration, not a voice surface (15 Oct 2026):
      // the calendar drives Support Desk SLAs as well as voice routing, so a
      // chat- or WhatsApp-only workspace configures it here.
      { id: "tn-hours", label: "Business hours", icon: "clock", to: "/voice/admin/hours", permission: "business_hours.manage" },
    ],
  },

  // Users — the workspace's own roster (invite, change role, remove). Like
  // Teams, the page existed at /users with no nav entry, so a tenant admin
  // holding users.manage had no way to find it.
  {
    group: null,
    permission: "users.manage",
    requiresMembership: true,
    items: [
      { id: "tn-users", label: "Users", icon: "customers", to: "/users", permission: "users.manage" },
    ],
  },

  // Audit Log — THIS workspace's own trail. Its only nav entry used to be
  // pf-audit inside the Platform group, which went to /admin: the page stayed
  // routed here and became reachable by typing the URL only, which is the same
  // half-broken state the note in filterNav describes. /ops shows the active
  // workspace's rows (audit-api.ts filters on req.tenantId when one is set);
  // the cross-tenant view of the same page is in /admin.
  {
    group: null,
    permission: "audit.read",
    items: [
      { id: "tn-audit", label: "Audit Log", icon: "book", to: "/audit", permission: "audit.read" },
    ],
  },

  // Profile — every user's own account page, and the last thing left of what
  // used to be the "Platform" group. Those eight governance surfaces (Tenants,
  // Platform Users, Roles, Tools, Demo Studio, Portfolio, AI Usage, the AI
  // ledger) moved to the /admin console, which is a separate build with its own
  // sign-in. Profile is not platform-owned, so it stays here, ungrouped: a
  // leaf with no permission, visible to everyone.
  //
  // Billing and the Audit Log stayed too, and are NOT duplicated — /admin
  // routes the same two pages, and each renders its cross-tenant view there
  // because api.ts sends no workspace header on that path.
  {
    group: null,
    items: [
      // The product launcher (Sunday 1731): every member, no permission, like
      // Profile. Opening a product is decided at /sso/authorize.
      { id: "pf-products", label: "Products", icon: "grid", to: "/products" },
      { id: "pf-profile", label: "Profile", icon: "settings", to: "/profile" },
    ],
  },
];

const ALL_LEAVES = NAV.flatMap((g) => g.items);
const COLLAPSE_KEY = "helix-shell-collapsed";

// The workspace list is fetched by EVERY Shell mount — every page navigation,
// and the route fallback that shows the real nav while a page's chunk loads.
// It changes when a workspace is created or its modules edited, which is
// rare, so one request serves every mount for TENANTS_TTL_MS, and the last
// answer seeds the next mount synchronously so the switcher never flashes.
const TENANTS_TTL_MS = 30_000;
let lastTenants: TenantOption[] | null = null;
let tenantsInFlight: { at: number; p: Promise<{ tenants: TenantOption[] }> } | null = null;
function loadTenantsShared(): Promise<{ tenants: TenantOption[] }> {
  if (tenantsInFlight && Date.now() - tenantsInFlight.at < TENANTS_TTL_MS) return tenantsInFlight.p;
  const entry = { at: Date.now(), p: opsFetch<{ tenants: TenantOption[] }>("/ops/api/tenants") };
  tenantsInFlight = entry;
  entry.p.then((r) => { lastTenants = r.tenants; }, () => { if (tenantsInFlight === entry) tenantsInFlight = null; });
  return entry.p;
}
/** Forget the cached list — call after creating or editing a workspace. */
export function invalidateTenantsCache(): void { tenantsInFlight = null; lastTenants = null; }
const NAV_OPEN_KEY = "helix-shell-nav-open";
const THEME_KEY = "engage-theme";

// Persisted inline expand/collapse choices for the grouped nav sections
// ({ [group label]: open }). Malformed/absent storage → all sections closed
// (the section holding the active route is force-opened on mount anyway).
function loadNavOpen(): Record<string, boolean> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(NAV_OPEN_KEY) ?? "null");
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, boolean>;
    }
  } catch { /* ignore */ }
  return {};
}

/**
 * EVERY DESTINATION THIS PERSON CAN REACH, grouped as the rail groups them.
 *
 * Exported so the role Home page and the rail answer the question from the SAME
 * arrays. The design's Home shows "every destination this role can reach" beside
 * the rail that shows them — two computations would be two chances to disagree,
 * and the whole value of that panel is that it is exhaustive.
 *
 * Recomputes the inputs from `me` rather than taking them as arguments: a caller
 * that had to assemble perms, membership, staff and extension flags itself would
 * be a second implementation of the rules, which is the same problem one level
 * down.
 */
export function destinationsFor(
  me: Me,
  activeSlug: string | null,
): { group: string | null; items: { id: string; label: string; to: string }[] }[] {
  const m =
    me.memberships.find((x) => x.slug === activeSlug) ??
    (me.memberships.length === 1 ? me.memberships[0] : undefined);
  const perms = new Set(m?.permissions ?? me.permissions ?? []);
  const membership = !!activeSlug && me.memberships.some((x) => x.slug === activeSlug);
  const staff = isPlatformStaff(me.platformRole ?? me.role);
  const tenantAdmin = m?.role === "admin" || me.platformRole === "owner";
  const billing = m?.billingEnabled !== false;
  return filterNav(NAV, perms, billing, membership, staff, !!me.hasExtension, tenantAdmin)
    .map((sec) => ({
      group: sec.group,
      items: sec.items.map((it) => ({ id: it.id, label: it.label, to: it.to })),
    }));
}

/**
 * WHAT A PERMISSION SET REACHES — the IA matrix's column.
 *
 * Same filterNav the rail uses, run against a role's keys instead of a person's.
 * The matrix exists to answer "who can see what", so computing it any other way
 * would produce a picture that could disagree with the menu it describes —
 * which is worse than no picture, because people would trust it.
 *
 * The non-permission flags are set to what makes the ROLE's reach legible
 * rather than any individual's:
 *   - hasExtension TRUE, because the softphone is gated on holding a line, not
 *     on a role. Marking it unreachable for every role would say something
 *     false about the access model.
 *   - membership TRUE, since a role is only ever held within a workspace.
 *   - staff / tenantAdmin derived from the role itself.
 */
export function destinationsForPermissions(
  permissions: string[],
  opts: { staff?: boolean; tenantAdmin?: boolean } = {},
): { group: string | null; items: { id: string; label: string; to: string }[] }[] {
  return filterNav(
    NAV, new Set(permissions), true, true,
    opts.staff ?? false, true, opts.tenantAdmin ?? false,
  ).map((sec) => ({
    group: sec.group,
    items: sec.items.map((it) => ({ id: it.id, label: it.label, to: it.to })),
  }));
}

/** Every destination in the product, grouped — the matrix's rows. Unfiltered on
 *  purpose: a row a role cannot reach is the whole point of the grid.
 *
 *  DE-DUPLICATED BY ID, and it has to be: Conversations is declared twice under
 *  opposite gates so it can sit in a different place for an agent than for
 *  everyone else (see the note in NAV). This function is the only consumer that
 *  does NOT run filterNav — which is what collapses the pair everywhere else —
 *  so without this the matrix would grow a second Conversations row and invite
 *  the reader to wonder which one their role really gets. There is one
 *  destination; the grid should say so. First declaration wins, so it keeps the
 *  place a reader with no extension sees. */
export function allDestinations(): { group: string | null; items: { id: string; label: string; to: string }[] }[] {
  const seen = new Set<string>();
  return NAV.map((sec) => ({
    group: sec.group,
    items: sec.items
      .filter((it) => !seen.has(it.id) && (seen.add(it.id), true))
      .map((it) => ({ id: it.id, label: it.label, to: it.to })),
  }));
}

// Permission-driven nav filtering (dynamic RBAC). A group is shown when its
// own permission (if any) is held OR at least one permissioned leaf survived —
// see the note at the bottom of filterNav; a leaf is shown when its permission
// (if any) is held. Module entitlement is encoded
// in the permission set itself — the server only grants voice.read/console.read/
// analytics.read when the active tenant has the module — so what a user SEES
// matches what it can USE, with no separate module gate. Billing keeps its
// per-tenant feature flag.
function filterNav(
  nav: NavGroup[],
  perms: Set<string>,
  billingEnabled: boolean,
  isMemberOfActive: boolean,
  platformStaff = false,
  hasExtension = false,
  tenantAdmin = false,
): NavGroup[] {
  // IS THIS PERSON'S JOB THE DESK? Not "do they own a phone".
  //
  // The agent pillar used to be gated on `hasExtension` alone, and that read one
  // fact for another. A supervisor needs a line of their OWN — listening,
  // whispering and barging all ring the supervisor's phone first, so monitoring
  // is impossible without one (server/ops/voice-floor-api.ts:372 refuses). Give
  // them that line and, under the old rule, they also got Agent desktop,
  // Tickets, Voicemail, Outbound preview and My performance: five destinations
  // belonging to a job they do not do. The only way to take the desk away was to
  // take the phone away, which took monitoring with it.
  //
  // So the desk asks about the ROLE and the phone stays a phone. A tenant admin
  // or platform-staff account is never on the queue here, whatever hardware it
  // holds; they reach the softphone through Supervisor → Agents & states, which
  // is where they connect it and where monitoring lives.
  //
  // This also makes an assumption the nav was ALREADY relying on true again —
  // the note by vt-voicemail says the agent entry is one "an admin never has".
  // That stopped being true the moment an admin was given a line; gating on the
  // role rather than the hardware makes it true again by construction.
  // A SUPERVISOR IS NOT ON THE DESK EITHER, AND THEY MUST HOLD A PHONE.
  //
  // voice.supervise is authority over other people's calls, and monitoring rings
  // the supervisor's OWN extension first (server/ops/voice-floor-api.ts:372
  // refuses without one) — so a supervisor necessarily has a line. Without this
  // clause that line handed them the whole agent desk: Agent desktop, Tickets,
  // Voicemail, Outbound preview, My performance, none of which is their job, and
  // a rail of two pillars became three the moment they were given the phone they
  // need to do the work.
  //
  // Same distinction as the tenantAdmin clause: holding a phone and working the
  // queue are different facts, and this nav had been reading one for the other.
  const onAgentDesk =
    hasExtension && !tenantAdmin && !platformStaff && !perms.has("voice.supervise");
  const out: NavGroup[] = [];
  for (const sec of nav) {
    // BILLING IS NOT FOR EVERY MEMBER OF A TENANT.
    //
    // The feature flag alone was the whole gate, on the reasoning in 0084 that
    // "billing VISIBILITY is governed by the per-tenant billingEnabled flag …
    // never by a permission". That decision stands and is not being reversed
    // here — no permission key comes back. What it did not anticipate is a
    // TENANT USER: it was written when the people scoped to a workspace were
    // admins and CSRs, and it now shows a live-desk agent the workspace's spend,
    // rate card and invoice.
    //
    // The design is explicit that an agent never sees "any org-wide report", and
    // a bill is the most org-wide thing in the product. So the flag still decides
    // whether billing EXISTS for a tenant; the role decides who in that tenant
    // looks at it.
    //
    // Deliberately not billing.manage: that is the right to edit a rate card, and
    // a tenant admin is supposed to READ their own bill without holding it.
    if (sec.feature === "billing" && (!billingEnabled || !(tenantAdmin || platformStaff))) continue;
    if (sec.requiresMembership && !isMemberOfActive && !(sec.orPlatformStaff && platformStaff)) continue;
    const items = sec.items.filter((leaf) => {
      if (leaf.platformStaffOnly && !platformStaff) return false;
      // The softphone belongs to whoever holds the extension, not to a role. An
      // admin or supervisor without one cannot register and cannot receive a
      // call, so listing it offers a desk that does not exist — and implies
      // calls might arrive there. Permissions cannot express this: voice.read
      // is about seeing the product, not about having a phone.
      if (leaf.requiresAgentDesk && !onAgentDesk) return false;
      // The inverse gate. See hiddenOnAgentDesk on NavLeaf: it exists so one
      // destination can sit in two places for two kinds of reader, and the pair
      // is only ever correct if these two lines read the SAME value — which is
      // why both say onAgentDesk and neither says hasExtension.
      if (leaf.hiddenOnAgentDesk && onAgentDesk) return false;
      return leaf.anyPermission
        ? leaf.anyPermission.some((p) => perms.has(p))
        : !leaf.permission || perms.has(leaf.permission);
    });
    if (items.length === 0) continue;

    // A GROUP PERMISSION IS A SHORTCUT, NOT A WALL.
    //
    // This used to skip the whole group before its leaves were considered, which
    // hid pages from people who held the permission for them. The case that
    // proved it: Ops Manager carries audit.read — its own description says
    // "audit and cost visibility" — but the Audit Log sat inside a "Platform"
    // group gated on tenants.manage, so it was unreachable from the nav for
    // exactly the role that exists to read it. The page worked if you typed the
    // URL, which is the worst kind of half-broken. (That group has since moved
    // to the /admin console and the Audit Log is its own pill, so the example is
    // history; the rule it bought is not.)
    //
    // Now a group appears when its own permission is held OR when at least one
    // leaf that CARRIES a permission survived the filter. That reveals nothing
    // new: every leaf is independently gated, so the group is only ever a
    // container for pages the user could already open.
    //
    // The "carries a permission" part matters. Some leaves (Profile) have none
    // and are visible to everyone; counting those would make every group with
    // one appear for every user, which is the opposite bug.
    const hasPermittedLeaf = items.some((l) => l.permission || l.anyPermission);
    // orTenantAdmin: a tenant admin of the active workspace sees the group
    // without holding its permission — mirrors the server's
    // requireAdminOnTenant (tenant Settings → API keys).
    if (
      sec.permission && !perms.has(sec.permission) &&
      !(sec.orTenantAdmin && tenantAdmin) && !hasPermittedLeaf
    ) continue;

    out.push({ ...sec, items });
  }
  return out;
}

interface TenantOption {
  id: string;
  slug: string;
  name: string;
  enabledModules?: string[];
  billingEnabled?: boolean;
  // Delivered by /ops/api/tenants (tenants-api.ts) — used to white-label the
  // header for tenant personas.
  logoUrl?: string | null;
  primaryBrandColor?: string | null;
}

// longest-prefix match of the current path against a leaf route
function activeLeafId(pathname: string): string | null {
  let best: { id: string; len: number } | null = null;
  for (const leaf of ALL_LEAVES) {
    const base = leaf.to;
    if (pathname === base || pathname.startsWith(base + "/") || (base === "/home" && pathname === "/")) {
      if (!best || base.length > best.len) best = { id: leaf.id, len: base.length };
    }
  }
  return best?.id ?? null;
}

// ── Persistent frame vs. per-page body ───────────────────────────────────────
// Every page renders its own <Shell>. Historically that meant every navigation
// unmounted and remounted the whole chrome — header, nav rail, workspace
// switcher — so the rail was rebuilt on each click (its state reset, its
// tenant list refetched, its open group re-animated). App.tsx now mounts ONE
// <ShellFrame> around the authenticated routes; a <Shell> rendered inside it
// contributes only its section bar + work area, and the chrome stays put.
// Outside a frame (a page mounted standalone) <Shell> still draws the full
// chrome, so the props contract and every existing page are unchanged.
// Pinned on globalThis so a Vite hot reload of this module reuses the SAME
// context object. Without that, editing Shell.tsx with the dev server running
// re-created the context: the already-mounted frame held the old one, every
// re-imported page Shell read the new one (default: false), and each page drew
// a second full nav rail inside the frame until a hard reload (24 Sep 2026).
// Exported so ops/admin/AdminShell.tsx can declare itself a frame. A page
// inside a frame renders only its PageBody, so every page moved to /admin in
// B5 keeps rendering <Shell> and needs no change beyond its import path.
export const ShellFrameContext: ReturnType<typeof createContext<boolean>> =
  ((globalThis as { __helixShellFrameCtx?: ReturnType<typeof createContext<boolean>> }).__helixShellFrameCtx ??= createContext(false));

/** Section bar (crumbs / title / actions) + the scrolling work area. The
 *  per-page part of the shell — everything that changes between pages. */
function PageBody({ title, crumbs, subtitle, actions, children, fullBleed = false }: Omit<Props, "me" | "onLoggedOut">) {
  return (
    <>
      {(title || (crumbs && crumbs.length > 0) || actions) && (
        <div className="section-bar">
          <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
            {crumbs && crumbs.length > 0 && (
              <div className="sb-crumb">
                {crumbs.map((c, i) => (
                  <span key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    {i > 0 && <ShellIcon name="chevron" size={11} />}
                    {c.to && i < crumbs.length - 1 ? (
                      <Link to={c.to} style={{ color: "inherit", textDecoration: "none" }}>{c.label}</Link>
                    ) : (
                      <b>{c.label}</b>
                    )}
                  </span>
                ))}
              </div>
            )}
            {title && <h1 className="sb-title">{title}</h1>}
            {subtitle && <span className="sb-sub">{subtitle}</span>}
          </div>
          {actions && <div className="sb-actions">{actions}</div>}
        </div>
      )}
      <div className={"work" + (fullBleed ? " fullbleed" : "")}>{children}</div>
    </>
  );
}

/** The one persistent chrome. Mount it ONCE (App.tsx) around the routes;
 *  pages inside it render <Shell> as before and slot into its main column. */
export function ShellFrame({ me, onLoggedOut, children }: { me: Me; onLoggedOut: Props["onLoggedOut"]; children: ReactNode }) {
  return (
    <ShellFrameContext.Provider value={true}>
      <ShellChrome me={me} onLoggedOut={onLoggedOut}>{children}</ShellChrome>
    </ShellFrameContext.Provider>
  );
}

export default function Shell({ me, onLoggedOut, ...page }: Props) {
  const framed = useContext(ShellFrameContext);
  if (framed) return <PageBody {...page} />;
  return (
    <ShellChrome me={me} onLoggedOut={onLoggedOut}>
      <PageBody {...page} />
    </ShellChrome>
  );
}

// The PERSISTENT half only: sidebar, top bar, theme. The per-page half — title,
// crumbs, subtitle, actions, fullBleed — belongs to PageBody above, which is
// what <Shell> renders inside this. Taking those props here too would be a
// second place deciding what a page's header says.
//
// Built on the template's sidebar (next-shadcn-admin-dashboard at bff60d4:
// dashboard/layout.tsx, _components/sidebar/{app-sidebar,nav-main,nav-user},
// _components/header/{theme-switcher,account-switcher}). The slots — what sits
// in the sidebar's header and footer and in the top bar — were confirmed by
// the user on 5 Oct 2026 (helix-ui-migration, "The shell's slots"). Record:
// docs/testing/ui-migration/019-shell-tsx.md.
//
// onLoggedOut is in the type because ShellFrame passes it, but is deliberately
// not destructured: Shell's only use for it was the sign-out it no longer owns
// — that lives at the /logout route (pages/Logout.tsx), reachable from the
// address bar when the chrome will not respond.
function ShellChrome({
  me,
  children,
}: Pick<Props, "me" | "onLoggedOut" | "children">) {
  // No useNavigate here: the only client-side navigation Shell itself performed
  // was the post-logout push, which now belongs to the /logout route.
  const location = useLocation();

  // The collapsed choice drives the template's SidebarProvider through
  // open/onOpenChange; it stays under the same key, never the template's
  // sidebar_state cookie.
  const [collapsed, setCollapsed] = useState<boolean>(
    () => typeof window !== "undefined" && window.localStorage.getItem(COLLAPSE_KEY) === "1",
  );
  useEffect(() => {
    window.localStorage.setItem(COLLAPSE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // THE THEME: light, dark or system (the template's cycle). Unset or unknown
  // means system, so a stored light/dark keeps its meaning and everyone else
  // starts from the computer's setting. ops/index.html applies the same choice
  // before first paint; this keeps <html> and .helix in step after that.
  const { themeMode, theme, cycleTheme } = useShellTheme();

  // tenants (workspace switcher)
  const [tenants, setTenants] = useState<TenantOption[]>(() => lastTenants ?? []);
  // The chrome outlives navigation now, so the active scope can't be read once
  // at mount: App's session re-check may correct a stale slug after the frame
  // is already up, and the switcher rewrites it. setActiveTenantSlug dispatches
  // `chloe-ops:tenant-changed` — follow it.
  const [activeSlug, setActiveSlug] = useState<string | null>(() => getActiveTenantSlug());
  useEffect(() => {
    const onChange = () => setActiveSlug(getActiveTenantSlug());
    window.addEventListener("chloe-ops:tenant-changed", onChange);
    return () => window.removeEventListener("chloe-ops:tenant-changed", onChange);
  }, []);
  // Refresh the workspace list on every navigation, as the per-page mount used
  // to; the shared loader dedupes that to one request per TENANTS_TTL_MS.
  useEffect(() => {
    loadTenantsShared()
      .then((r) => {
        setTenants(r.tenants);
        // Cache every workspace's module set so a reload (or switch) paints
        // the correct nav synchronously instead of flashing all products.
        try {
          for (const t of r.tenants) {
            if (t.enabledModules) {
              window.localStorage.setItem(
                `chloe-ops:modules:${t.slug}`,
                JSON.stringify(t.enabledModules),
              );
            }
            window.localStorage.setItem(
              `chloe-ops:billing:${t.slug}`,
              t.billingEnabled === false ? "0" : "1",
            );
          }
        } catch { /* ignore */ }
      })
      .catch(() => setTenants([]));
  }, [location.pathname]);
  const activeTenant = useMemo(
    () => tenants.find((t) => t.slug === activeSlug) ?? null,
    [tenants, activeSlug],
  );
  const onTenantChange = useCallback((slug: string | null) => {
    setActiveTenantSlug(slug);
    window.location.href = "/ops";
  }, []);

  // The effective permission set for the active scope — drives nav visibility.
  // The server delivers it in the auth payload: each membership carries its own
  // set (correct on first paint, no fetch), and the top-level `permissions` set
  // reflects the active-tenant header (this is how a superadmin — who has no
  // memberships — gets their scoped view). Module entitlement is already baked
  // in (voice.read etc. only present when the tenant has the module).
  // The sole-membership fallback (no active slug yet — a fresh login and a
  // logout both clear it) lives in activePermissions, which every gated
  // surface now shares. Mirror billingEnabled's fallback below.
  const activePerms = useMemo<Set<string>>(
    () => activePermissions(me, activeSlug),
    [me, activeSlug],
  );

  // Per-tenant Billing kill-switch (platform-admin controlled). Owner on "All
  // workspaces" sees it (the page itself prompts to pick a tenant); otherwise
  // gate on the active tenant's flag. Falls back to a cached value while the
  // tenant list loads so the pill doesn't flash in/out.
  const billingEnabled = useMemo<boolean>(() => {
    if (me.platformRole === "owner") {
      if (!activeSlug) return true;
      if (activeTenant) return activeTenant.billingEnabled !== false;
      try {
        const v = window.localStorage.getItem(`chloe-ops:billing:${activeSlug}`);
        if (v != null) return v === "1";
      } catch { /* ignore */ }
      return true;
    }
    const m =
      me.memberships.find((x) => x.slug === activeSlug) ?? me.memberships[0];
    return m ? m.billingEnabled !== false : true;
  }, [me, activeSlug, activeTenant]);

  // Whether the current user is a tenant_users member of the active workspace.
  // True for Platform Staff / Tenant Admin (who join via membership); false for
  // the owner, who scopes via the switcher without a membership. Gates the
  // membership-holder surfaces (tenant Settings).
  const isMemberOfActive = useMemo(
    () => !!activeSlug && me.memberships.some((m) => m.slug === activeSlug),
    [me.memberships, activeSlug],
  );
  const platformStaff = isPlatformStaff(me.platformRole ?? me.role);
  // Admin OF THE ACTIVE WORKSPACE, not "an admin somewhere". Someone can be a
  // tenant admin on one workspace and a plain user on another, and the rail
  // reflects whichever one is in scope. The same test the server's
  // requireAdminOnTenant applies, resolved like activePermissions: the active
  // slug, else the sole membership.
  const tenantAdminHere = useMemo(() => {
    // A membership role is only "admin" | "user" — the platform owner has no
    // membership at all and scopes through the switcher, so they are checked
    // separately rather than looked for in this list.
    const m =
      me.memberships.find((x) => x.slug === activeSlug) ??
      (me.memberships.length === 1 ? me.memberships[0] : undefined);
    return m?.role === "admin" || me.platformRole === "owner";
  }, [me.memberships, me.platformRole, activeSlug]);
  const visibleNav = useMemo(
    () => filterNav(NAV, activePerms, billingEnabled, isMemberOfActive, platformStaff, !!me.hasExtension, tenantAdminHere),
    [activePerms, billingEnabled, isMemberOfActive, platformStaff, me.hasExtension, tenantAdminHere],
  );

  // WHO THIS PERSON IS HERE, and where their shell starts.
  //
  // This used to also carry "N of M destinations · P permissions" for the rail
  // footer, counted from the same arrays the rail renders. The footer went when
  // the rail was matched to dev (the old NavRail, removed in pass 020); the counts
  // went with it rather than being computed for nobody. /ia-matrix still answers
  // the same question, for every role at once.
  //
  // What remains feeds the IA matrix's "Back to <role>" link.
  const railFooter = useMemo(() => {
    // The role the ACTIVE workspace grants, which is the one the shell reflects.
    // Falling back to the platform role matters for the owner, who scopes
    // through the switcher without a membership at all.
    const membership = me.memberships.find((m) => m.slug === activeSlug);
    const role = membership?.role ?? me.platformRole ?? me.role ?? "user";
    const label = role === "owner" ? "Owner"
      : role === "admin" ? "Admin"
      : platformStaff ? "Account Manager"
      : me.hasExtension ? "Agent"
      : "User";
    // Where this role's shell starts. THE SAME FUNCTION the post-login redirect
    // uses — not "the first destination in the nav", which is what this was and
    // which is Home for every role, because Home is the first leaf in the rail.
    // "Back to Agent" therefore returned an agent to Home rather than to their
    // desk. A landing is a statement about what a role's day starts with; it is
    // not derivable from menu order.
    const landing = landingFor(me);
    return { label, landing };
  }, [me, activeSlug, platformStaff]);

  const activeId = activeLeafId(location.pathname);

  // (`hereLabel` lived here: the name of the page the matrix was opened FROM,
  // so the way back could say "Back to Tickets". Only the floating shortcut ever
  // set that origin, and it is gone, so nothing produced the label any more.
  // The Back button on /ia-matrix now falls back to the role's landing, which is
  // what it already did for a deep link carrying no state.)
  const activeGroup = useMemo(
    () => NAV.find((s) => s.group && s.items.some((it) => it.id === activeId))?.group ?? null,
    [activeId],
  );

  // Sign-out lives at the /logout ROUTE (ops/src/pages/Logout.tsx), not in a
  // handler here. Shell had its own copy, the softphone card had another, and
  // each could break in its own way — which is what happened: the request
  // stopped reaching the server entirely and the audit log showed nothing.
  // One implementation, reachable from the address bar when the chrome won't.

  // The workspace control in the top bar names the workspace in scope at every
  // width and in every rail state. With none scoped (owner on "All
  // workspaces") it says so.
  const deploymentName = activeTenant?.name ?? (me.platformRole === "owner" ? "All workspaces" : "Workspace");

  // Who watches the team queue. Platform staff always do. A tenant user is
  // scoped to their own assignments unless they are an admin of the workspace
  // currently selected — membership role, not the platform role, is what says
  // "you are responsible for this team's inbox".
  const notificationTeamScope =
    isPlatformStaff(me.platformRole ?? me.role) ||
    (me.memberships ?? []).some((m) => m.slug === activeSlug && m.role === "admin");

  return (
    <div className={"helix" + (collapsed ? " collapsed" : "")} data-theme={theme}>
      {/* The template wraps its whole app in one (app/layout.tsx:44); the
          sidebar's rows are its only tooltips so far, so it sits here. */}
      <TooltipProvider>
      <SidebarProvider
        open={!collapsed}
        onOpenChange={(open) => setCollapsed(!open)}
        className="min-h-0 flex-1"
        style={{ "--sidebar-width": "17rem" } as React.CSSProperties}
      >
        <CloseSidebarSheetOnNavigate pathname={location.pathname} />
        <Sidebar collapsible="icon">
          <SidebarHeader>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip="Helix CX AI">
                  <Link to="/">
                    <LetterMark letter="H" />
                    <span className="text-base font-semibold">Helix CX AI</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarHeader>
          <SidebarContent>
            <SidebarNav nav={visibleNav} activeId={activeId} activeGroup={activeGroup} />
          </SidebarContent>
          <SidebarFooter>
            <SidebarUser me={me} roleLabel={friendlyRole(me, activeSlug)} />
          </SidebarFooter>
        </Sidebar>

        <SidebarInset className="main min-h-0 min-w-0 overflow-hidden [--dashboard-header-height:--spacing(12)]">
          <header className="flex h-12 shrink-0 items-center gap-2 border-b">
            <div className="flex w-full items-center justify-between gap-2 px-4 lg:px-6">
              <div className="flex min-w-0 flex-1 items-center gap-1 lg:gap-2">
                <SidebarTrigger className="-ml-1" title="Toggle navigation" aria-label="Toggle navigation" />
                <Separator
                  orientation="vertical"
                  className="mx-2 data-[orientation=vertical]:h-4 data-[orientation=vertical]:self-center"
                />
                {/* A bounded slot: the search box's own style was written for
                    the rail (flex: 1). Its own pass moves it onto the
                    template's search trigger and dialog. */}
                <div className="flex min-w-0 max-w-sm flex-1">
                  <GlobalSearchPalette />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ThemeControl mode={themeMode} onCycle={cycleTheme} />
                {/* Real bell, not a placeholder. Team scope = platform staff, or a
                    tenant admin in the active workspace: those viewers watch the whole
                    queue. A tenant `user` (CSR) sees only their own assignments, which
                    is what tenant_users.role='user' means everywhere else too. */}
                <NotificationBell meId={me.id} teamScope={notificationTeamScope} />
                <WorkspaceControl
                  me={me}
                  name={deploymentName}
                  tenants={tenants}
                  activeSlug={activeSlug}
                  switchable={me.platformRole === "owner" || tenants.length > 1}
                  onTenantChange={onTenantChange}
                />
              </div>
            </div>
          </header>
          {children}
        </SidebarInset>
      </SidebarProvider>
      </TooltipProvider>

      {/* IA MATRIX — NO LONGER FLOATED ON EVERY SCREEN (removed on request).
          The handoff put it on every page, on the reasoning that the access
          model is something you check while looking at something else. In use
          it read as a permanent widget sitting over the corner of every screen,
          which is a high price for a page most people open rarely.

          The page itself is untouched and still reachable at /ia-matrix — only
          the floating shortcut is gone. Restore it by bringing back the `else`
          branch that rendered a `mx-fab` Link to /ia-matrix with the same origin
          state this one reads.

          WHAT STAYS: the Back button, and only on the matrix page. Removing that
          too would leave anyone who deep-links to /ia-matrix with no way out
          except the browser control — the shortcut is a convenience, an exit is
          not.

          BACK RETURNS TO THE PAGE YOU LEFT. It used to go to the role's landing,
          on the reasoning that you might have arrived from anywhere. That is
          true and it is not the common case: the common case is being on a page,
          checking who reaches what, and wanting to carry on where you were.
          Sending an agent from Tickets to their desk loses their place for no
          gain.

          The origin travels in router state rather than a query string — it is
          navigation bookkeeping, not part of the address — and the LABEL travels
          with it, so the button names where it actually goes. A deep link
          straight to /ia-matrix carries no state, and then it falls back to the
          role's landing, which is the only sensible answer when there is no
          page to return to. */}
      {location.pathname === "/ia-matrix" ? (
        <Link className="mx-fab"
          to={(location.state as MatrixOrigin | null)?.from ?? railFooter.landing ?? "/home"}
          title={`Return to ${(location.state as MatrixOrigin | null)?.label ?? railFooter.label}`}>
          <ShellIcon name="grid" size={14} />
          Back to {(location.state as MatrixOrigin | null)?.label ?? railFooter.label}
        </Link>
      ) : null}
    </div>
  );
}

// ── Theme: light, dark or system ─────────────────────────────────────────────
// One key (engage-theme), the template's cycle. ops/index.html repeats the
// read before first paint — keep the two in step.
export type ThemeMode = "light" | "dark" | "system";
const THEME_CYCLE: readonly ThemeMode[] = ["light", "dark", "system"];

function readThemeMode(): ThemeMode {
  try {
    const v = window.localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark") return v;
  } catch { /* ignore */ }
  return "system";
}

function resolveTheme(mode: ThemeMode): "light" | "dark" {
  if (mode !== "system") return mode;
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  } catch {
    return "light";
  }
}

/** The `dark` class and color-scheme on <html>, where the template's `dark:`
 *  variant, theme.css's dark block and every portalled overlay read them. */
function applyThemeToDocument(theme: "light" | "dark"): void {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.style.colorScheme = theme;
}

/** The theme state both frames share (/ops here, /admin in AdminShell.tsx):
 *  the stored mode, what it resolves to, and the cycle. Keeps <html> in step. */
export function useShellTheme(): { themeMode: ThemeMode; theme: "light" | "dark"; cycleTheme: () => void } {
  const [themeMode, setThemeMode] = useState<ThemeMode>(readThemeMode);
  const theme = resolveTheme(themeMode);
  useEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);
  const cycleTheme = useCallback(() => {
    setThemeMode((m) => {
      const next = THEME_CYCLE[(THEME_CYCLE.indexOf(m) + 1) % THEME_CYCLE.length];
      try { window.localStorage.setItem(THEME_KEY, next); } catch { /* ignore */ }
      return next;
    });
  }, []);
  return { themeMode, theme, cycleTheme };
}

export function ThemeControl({ mode, onCycle }: { mode: ThemeMode; onCycle: () => void }) {
  const resolved = resolveTheme(mode);
  return (
    <Button
      size="icon"
      onClick={onCycle}
      title="Toggle theme"
      aria-label={`Current theme: ${mode}. Click to cycle themes`}
    >
      {mode === "system" ? <Monitor /> : resolved === "dark" ? <Sun /> : <Moon />}
    </Button>
  );
}

// The template's sheet stays open over the page a phone user just picked;
// Helix closes it, so the page they chose is what they see.
export function CloseSidebarSheetOnNavigate({ pathname }: { pathname: string }) {
  const { setOpenMobile } = useSidebar();
  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);
  return null;
}

/** A letter on the template's small square (nav-main.tsx's
 *  CollapsedIconFallback): the Helix mark, and a nav icon's fallback. */
export function LetterMark({ letter }: { letter: string }) {
  return (
    <span className="flex size-4 shrink-0 items-center justify-center rounded-xs text-[10px] font-medium outline">
      {letter}
    </span>
  );
}

// Human-readable role name for the header chip. Prefer the dynamic role name
// from the user's assignment (so renaming a role in the Roles UI shows up);
// fall back to the legacy label mapping when it's absent.
function friendlyRole(me: Me, activeSlug?: string | null): string {
  if (me.roleName) return me.roleName;
  const role = me.platformRole ?? me.role;
  if (role === "owner") return me.platformRoleLabels?.admin ?? "Platform Admin";
  if (role === "admin") return me.platformRoleLabels?.staff ?? "Platform Staff";
  // THE ACTIVE WORKSPACE'S ROLE, not "a role somewhere".
  //
  // This used to ask whether ANY membership was admin, so someone who is a
  // tenant admin on one workspace and a plain user on another read as "Tenant
  // Admin" while scoped to the one where they are neither — the same class of
  // mistake as the billing gate. The header is a statement about the workspace
  // in scope, so it has to be answered from that membership.
  const here = activeSlug ? me.memberships?.find((m) => m.slug === activeSlug) : undefined;
  if (here) return here.role === "admin" ? "Tenant Admin" : "Tenant User";
  if (me.memberships?.some((m) => m.role === "admin")) return "Tenant Admin";
  return "Tenant User";
}

// ── Top bar, right end: the workspace control ───────────────────────────────
// The template's account-switcher avatar, with the workspace's name beside it.
// A menu only when there is something to switch to; the menu is Helix's own
// (search, Enter picks the first match, Escape), restyled by shell-helix.css.
function WorkspaceControl({
  me,
  name,
  tenants,
  activeSlug,
  switchable,
  onTenantChange,
}: {
  me: Me;
  name: string;
  tenants: TenantOption[];
  activeSlug: string | null;
  switchable: boolean;
  onTenantChange: (slug: string | null) => void;
}) {
  const [wsOpen, setWsOpen] = useState(false);
  const [wsQuery, setWsQuery] = useState("");
  const wsRef = useRef<HTMLDivElement>(null);
  const wsSearchRef = useRef<HTMLInputElement>(null);

  // "All workspaces" and the tenants live in ONE ordered list so the filter,
  // the Enter key and the rendered rows can never disagree about what the
  // first match is. slug === null is the all-workspaces row.
  const wsOptions = useMemo(() => {
    const rows: { key: string; slug: string | null; name: string }[] =
      me.platformRole === "owner" ? [{ key: "__all__", slug: null, name: "All workspaces" }] : [];
    for (const t of tenants) rows.push({ key: t.id, slug: t.slug, name: t.name });
    const q = wsQuery.trim().toLowerCase();
    if (!q) return rows;
    // Slug is matched too: staff know tenants by slug more often than by
    // display name (e.g. "cocomoon" vs "Coco Moon").
    return rows.filter((r) => r.name.toLowerCase().includes(q) || (r.slug ?? "").toLowerCase().includes(q));
  }, [tenants, wsQuery, me.platformRole]);

  // Reset + focus the filter every time the switcher opens. A query left over
  // from the last visit would silently hide workspaces on reopen.
  useEffect(() => {
    if (!wsOpen) return;
    setWsQuery("");
    const id = window.setTimeout(() => wsSearchRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [wsOpen]);

  // Close the switcher on an outside click — NOT on mouse-leave, which
  // collapsed the menu the instant the pointer moved toward an item.
  useEffect(() => {
    if (!wsOpen) return;
    function onDown(e: MouseEvent) {
      if (wsRef.current && !wsRef.current.contains(e.target as Node)) setWsOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [wsOpen]);

  const face = (
    <>
      <Avatar className="size-8 rounded-lg">
        <AvatarFallback className="rounded-lg">{(name.trim().charAt(0) || "W").toUpperCase()}</AvatarFallback>
      </Avatar>
      <span className="max-w-48 truncate text-sm font-medium">{name}</span>
    </>
  );

  if (!switchable) {
    return <div className="flex min-w-0 items-center gap-2">{face}</div>;
  }

  return (
    <div className="relative" ref={wsRef}>
      <Button
        variant="ghost"
        className="h-9 gap-2 px-1.5"
        onClick={() => setWsOpen((v) => !v)}
        title="Switch workspace"
        aria-expanded={wsOpen}
      >
        {face}
        <ChevronDown className="size-4 text-muted-foreground" />
      </Button>
      {wsOpen && (
        <div className="pop-menu ws-menu" style={{ minWidth: 240 }}>
          <div className="nav-flyout-title" style={{ color: "var(--ink-3)" }}>Workspaces</div>
          <div className="ws-search">
            <ShellIcon name="search" size={14} />
            <input
              ref={wsSearchRef}
              type="text"
              value={wsQuery}
              placeholder="Search workspaces…"
              aria-label="Search workspaces"
              onChange={(e) => setWsQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  // Stop here so Escape closes the menu without also
                  // reaching any page-level Escape handler behind it.
                  e.stopPropagation();
                  setWsOpen(false);
                } else if (e.key === "Enter" && wsOptions.length > 0) {
                  onTenantChange(wsOptions[0].slug);
                }
              }}
            />
          </div>
          <div className="ws-list">
            {wsOptions.map((o) =>
              o.slug === null ? (
                <button key={o.key} className="um-item" onClick={() => onTenantChange(null)} style={activeSlug === null ? { color: "var(--violet-2)" } : undefined}>
                  <ShellIcon name="grid" size={15} /> {o.name}
                </button>
              ) : (
                <button key={o.key} className="um-item" onClick={() => onTenantChange(o.slug)} style={o.slug === activeSlug ? { color: "var(--violet-2)" } : undefined}>
                  <span className="tu-av" style={{ width: 20, height: 20, fontSize: 10 }}>{o.name.slice(0, 1).toUpperCase()}</span>
                  <span className="ws-name">{o.name}</span>
                </button>
              ),
            )}
            {wsOptions.length === 0 && <div className="ws-empty">No workspace matches “{wsQuery.trim()}”</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sidebar footer: the person, a menu with Profile / Sign out, powered-by ──
// The template's nav-user.tsx. Workspace-aware role: the same person is
// "Tenant Admin" on one workspace and "Agent" on another, and naming the wrong
// one is a factual error rather than a layout choice.
export function SidebarUser({
  me,
  roleLabel,
  profileHref,
}: {
  me: Me;
  roleLabel: string;
  /** /admin's router is based at /admin, so it links to /ops's Profile with an
   *  absolute href; /ops leaves this unset and uses its own route. */
  profileHref?: string;
}) {
  const { isMobile } = useSidebar();
  const initials = me.displayName.slice(0, 2).toUpperCase();
  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                title={me.email}
              >
                <Avatar className="size-8 rounded-lg">
                  <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{me.displayName}</span>
                  <span className="truncate text-xs text-muted-foreground">{roleLabel}</span>
                </div>
                <EllipsisVertical className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? "bottom" : "right"}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{me.displayName}</span>
                    <span className="truncate text-xs text-muted-foreground">{me.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                {profileHref ? (
                  <a href={profileHref}>
                    <ShellIcon name="settings" size={14} />
                    Profile
                  </a>
                ) : (
                  <Link to="/profile">
                    <ShellIcon name="settings" size={14} />
                    Profile
                  </Link>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {/* The /logout ROUTE, not a handler: one sign-out implementation,
                  and the address bar is a working fallback when the chrome won't
                  click (ops/src/pages/Logout.tsx). */}
              <DropdownMenuItem asChild>
                <Link to="/logout">
                  <ShellIcon name="logout" size={14} />
                  Sign out
                </Link>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      <div className="px-2 pb-1 text-xs text-muted-foreground group-data-[collapsible=icon]:hidden">
        Powered by <b className="font-medium text-foreground">GoZupees AI</b>
      </div>
    </>
  );
}

// ── Sidebar content: NAV as the template's nav-main.tsx draws it ────────────
// Leaves of an ungrouped section are top-level rows. A named group is a
// collapsible row whose pages hang below it; only one is open at a time, the
// one holding the current page opens on arrival, and opening one never
// navigates. On the collapsed icon rail a group opens its pages in a menu
// beside the rail instead. Every row here has already passed filterNav.
export function SidebarNav({
  nav,
  activeId,
  activeGroup,
}: {
  nav: NavGroup[];
  activeId: string | null;
  activeGroup: string | null;
}) {
  const { state, isMobile } = useSidebar();
  const collapsedDesktop = state === "collapsed" && !isMobile;

  // Persisted expand/collapse choices, written as before (the group holding
  // the active route is forced open on mount and whenever it changes).
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    const stored = loadNavOpen();
    if (activeGroup) stored[activeGroup] = true;
    return stored;
  });
  useEffect(() => {
    try { window.localStorage.setItem(NAV_OPEN_KEY, JSON.stringify(open)); } catch { /* ignore */ }
  }, [open]);
  useEffect(() => {
    if (activeGroup) setOpen((o) => (o[activeGroup] ? o : { ...o, [activeGroup]: true }));
  }, [activeGroup]);

  // Which group is expanded. Route-driven by default (opens the group you're
  // in), but user-toggleable so you can COLLAPSE the group you're inside.
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroup);
  useEffect(() => { setOpenGroup(activeGroup); }, [activeGroup]);

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {nav.map((sec, gi) => {
            if (!sec.group) {
              return sec.items.map((it) => (
                <SidebarMenuItem key={`${gi}:${it.id}`}>
                  <SidebarMenuButton asChild tooltip={it.label} isActive={activeId === it.id}>
                    <Link to={it.to}>
                      <ShellIcon name={it.icon} size={18} />
                      <span>{it.label}</span>
                    </Link>
                  </SidebarMenuButton>
                  {it.badge && <SidebarMenuBadge>{it.badge}</SidebarMenuBadge>}
                </SidebarMenuItem>
              ));
            }

            const group = sec.group;
            const isActive = group === activeGroup;

            if (collapsedDesktop) {
              return (
                <SidebarMenuItem key={gi}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <SidebarMenuButton tooltip={group} isActive={isActive}>
                        <ShellIcon name={sec.icon as string} size={18} />
                        <span>{group}</span>
                      </SidebarMenuButton>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side="right" align="start" sideOffset={12} className="w-48">
                      <DropdownMenuLabel>{group}</DropdownMenuLabel>
                      <DropdownMenuGroup>
                        {sec.items.map((it) => (
                          <div key={it.id}>
                            {it.heading && <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">{it.heading}</DropdownMenuLabel>}
                            <DropdownMenuItem asChild>
                              <Link
                                to={it.to}
                                aria-current={activeId === it.id ? "page" : undefined}
                                className="flex items-center gap-2"
                              >
                                <ShellIcon name={it.icon} size={16} />
                                <span>{it.label}</span>
                                {it.badge && <span className="ml-auto text-xs text-muted-foreground">{it.badge}</span>}
                              </Link>
                            </DropdownMenuItem>
                          </div>
                        ))}
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </SidebarMenuItem>
              );
            }

            const expanded = group === openGroup;
            return (
              <Collapsible
                key={gi}
                asChild
                open={expanded}
                // Toggle only: click an open group to collapse it; click a
                // closed one to reveal its pages. Never navigate from here —
                // the user picks a page by clicking it.
                onOpenChange={(next) => setOpenGroup(next ? group : null)}
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={group} isActive={isActive}>
                      <ShellIcon name={sec.icon as string} size={18} />
                      <span>{group}</span>
                      <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 motion-reduce:transition-none" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {sec.items.map((it) => (
                        <SidebarMenuSubItem key={it.id}>
                          {it.heading && (
                            <div className="flex h-8 items-center px-2 text-xs font-medium text-sidebar-foreground/70">
                              {it.heading}
                            </div>
                          )}
                          <SidebarMenuSubButton asChild isActive={activeId === it.id}>
                            <Link to={it.to}>
                              <ShellIcon name={it.icon} size={16} />
                              <span>{it.label}</span>
                            </Link>
                          </SidebarMenuSubButton>
                          {it.badge && (
                            <span className="pointer-events-none absolute top-1 right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium text-sidebar-foreground tabular-nums">
                              {it.badge}
                            </span>
                          )}
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
